import { CirclePlay } from 'lucide-react';
import type { FunctionComponent } from 'react';

import { ctw } from '@/common/utils/ctw';
import { buttonVariants, Card, CheckCircle, LockCircle } from '@/components';

type GatedContentCheckBarProps = {
  status: 'success' | 'failed';
  message?: string;
  sessionUrl: string | null;
  className?: string;
  isGatedContentCheckEnabled: boolean;
};

export const GatedContentCheckBar: FunctionComponent<GatedContentCheckBarProps> = ({
  status,
  message,
  sessionUrl,
  isGatedContentCheckEnabled,
  className,
}) => {
  const checkStatus = (() => {
    if (!isGatedContentCheckEnabled) {
      return 'Detected';
    }

    if (status === 'success') {
      return 'Successful';
    }

    if (status === 'failed') {
      return 'Failed';
    }
  })();
  const messageToDisplay = !isGatedContentCheckEnabled
    ? 'Activate "Gated Content Websites Scans" on report creation or by contacting sales.'
    : message;

  return (
    <Card className={ctw('mb-6 rounded-2xl bg-white px-5 py-2', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {status === 'success' && (
            <CheckCircle
              size={29}
              className={`stroke-success stroke-[0.1875rem]`}
              containerProps={{
                className: 'bg-success/20',
              }}
            />
          )}
          {(status === 'failed' || !isGatedContentCheckEnabled) && (
            <LockCircle
              size={29}
              className={`stroke-error stroke-[0.1875rem]`}
              containerProps={{
                className: 'bg-error/20',
              }}
            />
          )}
          <div className="flex items-center gap-6">
            <span className="text-sm font-semibold">Gated Content Check {checkStatus}</span>
            <span className="text-sm">{messageToDisplay}</span>
          </div>
        </div>
        {isGatedContentCheckEnabled && (
          <a
            href={sessionUrl ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={ctw(
              buttonVariants({
                variant: 'outline',
                size: 'sm',
                className: 'gap-1.5',
              }),
              {
                'pointer-events-none opacity-50': !sessionUrl,
              },
            )}
          >
            <CirclePlay className="h-4 w-4" />
            <span>Watch session</span>
          </a>
        )}
      </div>
    </Card>
  );
};

GatedContentCheckBar.displayName = 'GatedContentCheckBar';
